import type { RappelEffect } from '../../../data/full/superfan';
import { ticksIntoBeat } from '../../../shared/tempo';
import { hurtEnemy, keepWhere, touches } from '../../effects';
import type { SkillModule } from '../../skills/types';
import type { PlayerState, PropState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { frontRowPower, glowstickOf, plantStick } from '../attacks/glowstick';
import { ownerOf } from '../../props';

export const RAPPEL = 'rappel';

export const rappel: SkillModule<RappelEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state, tempo } = ctx;
    plantStick(ctx, player, player, player.aim);
    const call: PropState = {
      id: state.nextEntityId,
      kind: RAPPEL,
      playerId: player.id,
      phase: 'flying',
      x: player.x,
      y: player.y,
      prevX: player.x,
      prevY: player.y,
      vx: 0,
      vy: 0,
      damage: effect.damage * power,
      ticksLeft: tempo.ticksPerBeat - ticksIntoBeat(state.tick, tempo),
      hitsLeft: 0,
    };
    state.nextEntityId += 1;
    (state.props ??= []).push(call);
  },

  update(ctx) {
    const props = ctx.state.props;
    if (props === undefined) {
      return;
    }
    let burst = false;
    for (const prop of props) {
      if (prop.kind !== RAPPEL) {
        continue;
      }
      if (prop.ticksLeft === 0) {
        burstAll(ctx, prop);
        prop.ticksLeft = -1;
        burst = true;
      } else {
        prop.ticksLeft -= 1;
      }
    }
    if (burst) {
      keepWhere(props, (prop) => prop.kind !== RAPPEL || prop.ticksLeft >= 0);
      if (props.length === 0) {
        delete ctx.state.props;
      }
    }
  },
};

function rappelOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): RappelEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'rappel') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" calls no Rappel`);
  }
  return effect;
}

function rappelRadius(ctx: Pick<StepContext, 'content'>, owner: PlayerState): number {
  return rappelOf(ctx, owner).radius * statValue(owner, 'skillRadiusMul', 1);
}

function burstAll(ctx: StepContext, call: PropState): void {
  const { state, tempo } = ctx;
  const owner = ownerOf(state, call);
  const attack = glowstickOf(ctx, owner);
  const radius = rappelRadius(ctx, owner);
  for (const zone of state.zones ?? []) {
    if (zone.playerId !== owner.id || zone.definition.id !== attack.zone.id) {
      continue;
    }
    const damage = call.damage * frontRowPower(attack, state.core, zone);
    for (const enemy of state.enemies) {
      if (enemy.hp > 0 && touches(enemy, zone, radius)) {
        hurtEnemy(state, enemy, damage, owner.id, zone);
      }
    }
    zone.ticksLeft = zone.definition.durationBars * tempo.ticksPerBar;
  }
}
